import {
  createElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../lib/auth";
import {
  type RealtimeEventType,
} from "@peekop/core/contracts/events";
import type { PeekopRealtimeEvent } from "@peekop/core/contracts/realtime";
import { OBSERVED_EVENT_TYPES } from "../lib/realtime-events";
import {
  buildSseStreamUrl,
  requestSseSessionToken,
} from "../lib/sse-session";

type UnknownSSEEvent = {
  id: string;
  type: RealtimeEventType | string;
  requestId?: string;
  recipientId?: string;
  data: Record<string, unknown>;
  timestamp: string;
};

export type SSEEvent = PeekopRealtimeEvent | UnknownSSEEvent;

export type SSEConnectionState = "connecting" | "connected" | "reconnecting";

interface UseSSEReturn {
  events: SSEEvent[];
  isConnected: boolean;
  connectionState: SSEConnectionState;
  lastEvent: SSEEvent | null;
  unreadCount: number;
  clearUnread: () => void;
}

const MAX_RECONNECT_DELAY = 30_000; // 30 seconds cap
const BASE_RECONNECT_DELAY = 3_000; // 3 seconds initial

// The server sends NAMED SSE events (e.g. `event: request.signed\ndata: ...`).
// EventSource.onmessage ONLY fires for the default "message" type — any named
// event is silently dropped unless we register it via addEventListener. This
// list must stay in sync with the event types published by the API routes
// (apps/api/src/routes/*.ts -> createEvent("...") calls).
export { OBSERVED_EVENT_TYPES };

const SSEContext = createContext<UseSSEReturn | null>(null);
const IDLE_SSE_STATE: UseSSEReturn = {
  events: [],
  isConnected: false,
  connectionState: "connecting",
  lastEvent: null,
  unreadCount: 0,
  clearUnread: () => {},
};

function useSSEConnection(enabled: boolean): UseSSEReturn {
  const { getToken, isSignedIn } = useAuth();
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<SSEConnectionState>("connecting");
  const [lastEvent, setLastEvent] = useState<SSEEvent | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const proactiveRefreshRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const connectionGenerationRef = useRef(0);

  const connect = useCallback(async () => {
    if (!enabled) return;
    if (!isSignedIn) return;
    const generation = connectionGenerationRef.current + 1;
    connectionGenerationRef.current = generation;
    clearTimeout(reconnectTimeoutRef.current);
    clearTimeout(proactiveRefreshRef.current);
    setConnectionState(reconnectAttemptsRef.current > 0 ? "reconnecting" : "connecting");

    const token = await getToken();
    if (generation !== connectionGenerationRef.current) return;
    if (!token) return;

    const sseSession = await requestSseSessionToken(token);
    if (generation !== connectionGenerationRef.current) return;
    if (!sseSession) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = buildSseStreamUrl(sseSession.sessionToken);
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      if (generation !== connectionGenerationRef.current) {
        es.close();
        return;
      }
      setIsConnected(true);
      setConnectionState("connected");
      reconnectAttemptsRef.current = 0;

      // Proactively refresh 30s before the session token expires instead of
      // waiting for the EventSource to error — avoids the unsubscribe/resubscribe
      // churn visible in the server logs and prevents H2 stream cycling on other requests.
      clearTimeout(proactiveRefreshRef.current);
      const msUntilExpiry = new Date(sseSession.expiresAt).getTime() - Date.now();
      const refreshDelay = msUntilExpiry - 30_000;
      if (refreshDelay > 0) {
        proactiveRefreshRef.current = setTimeout(() => {
          connect();
        }, refreshDelay);
      }
    };

    const handleEvent = (event: MessageEvent) => {
      try {
        const parsed: SSEEvent = JSON.parse(event.data);
        // Defensive: ensure every event has a stable id. Server-originated
        // events from createEvent() always include one, but we synthesize a
        // fallback so downstream consumers that dedupe on `lastEvent.id`
        // (request-detail, EventListener toasts) never collapse two events
        // into "same undefined" and silently drop the second.
        if (!parsed.id) {
          parsed.id =
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `${parsed.type ?? "event"}:${parsed.timestamp ?? Date.now()}:${Math.random().toString(36).slice(2)}`;
        }
        setEvents((prev) => [parsed, ...prev].slice(0, 100)); // Keep last 100
        setLastEvent(parsed);
        setUnreadCount((prev) => prev + 1);
      } catch {
        // Ignore malformed events (e.g. heartbeat)
      }
    };

    // Register a listener for every named event type the API emits. Without
    // this, events like `event: request.signed` are sent by the server but
    // never surface on the client — onmessage only catches the default
    // "message" type. This was the root cause of the dashboard not updating
    // in real-time after a signer completed a document.
    for (const type of OBSERVED_EVENT_TYPES) {
      es.addEventListener(type, handleEvent as EventListener);
    }
    // Also keep onmessage as a safety net for any future unnamed events.
    es.onmessage = handleEvent;

    es.onerror = () => {
      if (generation !== connectionGenerationRef.current) {
        es.close();
        return;
      }
      setIsConnected(false);
      setConnectionState("reconnecting");
      es.close();
      clearTimeout(proactiveRefreshRef.current);

      // Exponential backoff: 3s → 6s → 12s → 24s → 30s (cap)
      const attempts = reconnectAttemptsRef.current;
      const delay = Math.min(
        BASE_RECONNECT_DELAY * Math.pow(2, attempts),
        MAX_RECONNECT_DELAY,
      );
      reconnectAttemptsRef.current = attempts + 1;

      if (attempts < 3) {
        console.log(`[SSE] Reconnecting in ${delay / 1000}s (attempt ${attempts + 1})`);
      } else if (attempts === 3) {
        console.log(`[SSE] Reconnecting in ${delay / 1000}s (attempt ${attempts + 1}) — suppressing further logs`);
      }
      // After attempt 3, silently retry without spamming console

      reconnectTimeoutRef.current = setTimeout(() => {
        if (generation !== connectionGenerationRef.current) return;
        connect();
      }, delay);
    };
  }, [enabled, isSignedIn, getToken]);

  useEffect(() => {
    if (!enabled) {
      connectionGenerationRef.current += 1;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      clearTimeout(reconnectTimeoutRef.current);
      clearTimeout(proactiveRefreshRef.current);
      reconnectAttemptsRef.current = 0;
      setEvents([]);
      setIsConnected(false);
      setConnectionState("connecting");
      setLastEvent(null);
      setUnreadCount(0);
      return;
    }

    connect();

    return () => {
      connectionGenerationRef.current += 1;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      clearTimeout(reconnectTimeoutRef.current);
      clearTimeout(proactiveRefreshRef.current);
    };
  }, [connect, enabled]);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return { events, isConnected, connectionState, lastEvent, unreadCount, clearUnread };
}

export function SSEProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const sse = useSSEConnection(enabled);
  return createElement(SSEContext.Provider, { value: sse }, children);
}

export function useSSE(): UseSSEReturn {
  const context = useContext(SSEContext);
  if (!context) {
    return IDLE_SSE_STATE;
  }
  return context;
}
